'use client';
import { useSidebar, Container as FabricContainer } from '@efcloud/ef-fabric';
import { styled, Box, TextField as MuiTextField, css, Autocomplete, useTheme, Theme, Avatar } from '@mui/material';
import { rgba } from 'polished';
import { type ComponentProps, forwardRef, PropsWithChildren } from 'react';

import Icon from 'components/Icon';
import { BORDER_GREY, BORDER_LIGHT, GREY_219, LINK_BLUE_ALT, SPACING_1, SPACING_7, TF_BLUE } from 'theme/legacy-tokens';

export const ELLIPSIS_CSS = `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Z_INDEX_500 = 500;
export const XL_BREAKPOINT = 1536;

export const SEARCH_BAR_WIDTH = '370px';

export const HeaderBar = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const MuiAvatar = styled(Avatar)`
  background-color: ${({ theme }) => theme.palette.common.black};
`;

export const MainWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor',
})<{ backgroundColor?: string }>`
  width: 100%;
  height: 100vh;
  position: relative;
  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      background-color: ${backgroundColor};
    `}
`;

export const Scrollable = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'mobile',
})<{ mobile?: boolean }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing(0)};
  left: ${({ theme }) => theme.spacing(0)};
  bottom: ${({ theme }) => theme.spacing(0)};
  right: ${({ theme }) => theme.spacing(0)};
  overflow: auto;

  ${({ mobile, theme }) =>
    mobile &&
    css`
      &::-webkit-scrollbar {
        background-color: ${theme.palette.common.white};
      }
    `}
`;

export const ScreenWrapper = ({
  children,
  backgroundColor,
  AppTitle: _apptitle,
}: PropsWithChildren<{ backgroundColor?: string; AppTitle?: string }>) => (
  <MainWrapper backgroundColor={backgroundColor}>
    <Scrollable id="scrollable"> {children} </Scrollable>
  </MainWrapper>
);

const DEFAULT_WIDTH = 1158;
const addPxIfNeeded = (n: number | string) => (typeof n === 'number' ? n + 'px' : n);

export const CenterItems = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'maxW',
})<{ maxW?: number | string }>`
  position: relative;
  margin: 0 auto;
  max-width: ${(props) => addPxIfNeeded(props.maxW ?? DEFAULT_WIDTH)};
`;

export const EditCenterItems = styled(CenterItems)`
  margin: -${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: 0;
  padding-bottom: 0;
  border-radius: ${({ theme }) => theme.shape.radii[4]}px;
  background-color: ${({ theme }) => theme.palette.common.white};
  box-sizing: content-box;
`;

export const ZIndex50 = styled(Box)`
  z-index: ${Z_INDEX_500};
  position: relative;
  width: 100%;
`;

export const FitContent = styled(ZIndex50)`
  width: fit-content;
`;

export const BodyWrapper = styled(Box)`
  position: relative;
  padding: ${({ theme }) => theme.spacing(2, 0)};
`;

export { AppLayout } from './AppLayout.component';
export { AppLayoutBase, AppLayoutRow } from './AppLayout.styled';

type ContainerProps = ComponentProps<typeof FabricContainer>;

export const Container = forwardRef<HTMLDivElement, ContainerProps>(({ children, ...rest }, ref) => {
  const { isCollapsed } = useSidebar();

  return (
    <FabricContainer ref={ref as ComponentProps<typeof FabricContainer>['ref']} {...rest} isCollapsed={isCollapsed}>
      {children}
    </FabricContainer>
  );
});

export const FullscreenCenter = styled(Box)`
  height: 100vh;
  width: 100%;
`;

export const CenteredBGWrapper = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100dvh;
  height: 100svh;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.primary[200]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LightTintBackground = styled(Box)`
  background-color: ${({ theme }) => theme.palette.primary[100]};
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: -1;
`;

export const ClampLines = styled('div')<{ clamp: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ clamp }) => clamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const Ellipsis = styled(Box)`
  width: 100%;
  ${ELLIPSIS_CSS}
`;

// Legacy exports migrated from sharedStyles.tsx

export const Content = styled(Box)`
  display: flex;
  flex-direction: row;
  padding-bottom: ${({ theme }) => theme.spacing(8)};
`;

export const Heading = styled('h1')`
  font-size: ${({ theme }) => theme.typography.h3.fontSize}px;
  align-self: center;
`;

export const InputIconContainer = styled(Box)`
  position: relative;
  left: -3px;
  top: 2px;
`;

const TableBase = {
  Table: styled('table')`
    position: relative;
    width: 100%;
    border-spacing: 0px 1em;
    border-collapse: separate;
    table-layout: fixed;
    .Mui-checked .MuiSvgIcon-root {
      color: ${({ theme }) => theme.palette.primary.main};
      fill: ${({ theme }) => theme.palette.primary.main};
    }
  `,
  Row: styled('tr')`
    &.active {
      td {
        border-color: ${({ theme }) => theme.palette.primary.main};
      }
    }
  `,
  Head: styled('th')<{ $width?: number; align?: string }>`
    text-align: ${(props) => (props.align ? props.align : 'left')};
    ${(props) => props.$width && `width: ${props.$width}px;`};
  `,
  Cell: styled('td')<{ align?: string }>`
    text-align: ${(props) => (props.align ? props.align : 'left')};
    position: relative;
    font-size: ${({ theme }) => theme.typography.body2.fontSize}px;
    border-top: solid 1px ${({ theme }) => theme.palette.divider};
    border-bottom: solid 1px ${({ theme }) => theme.palette.divider};
    vertical-align: middle;
    height: 54px;
    .Mui-disabled {
      filter: grayscale(0.9) brightness(1.5);
    }

    &:first-of-type {
      border-left: solid 1px ${({ theme }) => theme.palette.divider};
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &:last-child {
      border-right: solid 1px ${({ theme }) => theme.palette.divider};
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  `,
};

export const Table = {
  ...TableBase,
  CellEllipsis: styled(TableBase.Cell)`
    max-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    padding-right: ${({ theme }) => theme.spacing(2)};
  `,
  CellTime: styled(TableBase.Cell)`
    min-width: 145px;
  `,
  CellRight: styled(TableBase.Cell)`
    text-align: right;
  `,
};

// Additional legacy exports migrated from sharedStyles.tsx

export const BOX_SHADOW = `
  &:hover, &:focus {
    box-shadow: 0 2px 12px 0 ${LINK_BLUE_ALT};
    outline: 0;
  }
`;

export const getStylesForInvalid = (shadowColor: string, borderColor: string) =>
  `box-shadow: 0 4px 12px ${rgba(shadowColor, 0.5)};
   border-color: ${borderColor};`;

export interface InputStyleProps {
  focus?: boolean;
  invalid?: boolean;
  valid?: boolean;
  loading?: boolean;
  complete?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
}

export const InputStyles = ({ theme, ...props }: InputStyleProps & { theme: Theme }) =>
  css({
    background: theme.palette.common.white,
    boxSizing: 'border-box',
    borderRadius: '4px',
    color: theme.palette.common.black,
    fontSize: `${theme.typography.body2.fontSize}px`,
    lineHeight: '24px',
    padding: `${SPACING_7} ${theme.spacing(3)}`,
    border: `1px solid ${rgba('#191919', 0.5)}`,
    boxShadow: `0 2px 8px 0 ${rgba('#191919', 0.1)}`,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'border 0.25s linear, box-shadow 1s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    ...(props.complete && {
      borderColor: theme.palette.common.black,
      color: theme.palette.common.black,
      boxShadow: 'none',
    }),
    ...(props.invalid && {
      background: theme.palette.common.white,
      borderColor: theme.palette.error.main,
      boxShadow: 'none',
    }),
    '&:focus': {
      outline: 'none',
      ...(props.focus && {
        boxShadow: `0 4px 12px ${rgba(TF_BLUE, 0.5)}`,
        borderColor: TF_BLUE,
      }),
      ...(props.focus &&
        props.invalid && {
          boxShadow: `0 4px 12px ${rgba('#D1334A', 0.5)}`,
          borderColor: '#D1334A',
        }),
      ...(props.invalid && {
        boxShadow: `0 4px 12px ${rgba('#D1334A', 0.5)}`,
        borderColor: '#D1334A',
      }),
    },
    ...(props.focus && {
      boxShadow: `0 4px 12px ${rgba(TF_BLUE, 0.5)}`,
      borderColor: TF_BLUE,
    }),
    ...(props.focus &&
      props.invalid && {
        boxShadow: `0 4px 12px ${rgba('#D1334A', 0.5)}`,
        borderColor: '#D1334A',
      }),
    ...(props.invalid && {
      color: theme.palette.error.main,
    }),
    '&:disabled': {
      boxShadow: 'none',
      background: BORDER_LIGHT,
      borderColor: rgba('#191919', 0.1),
      color: rgba('#191919', 0.5),
      pointerEvents: 'none' as const,
    },
    ...(props.readOnly && {
      boxShadow: 'none',
      background: theme.palette.common.white,
      borderColor: BORDER_GREY,
      color: rgba('#191919', 0.5),
    }),
    ...((props.valid || props.invalid) && {
      paddingRight: theme.spacing(5),
    }),
    '&::placeholder': {
      color: theme.palette.common.black,
      opacity: 1,
    },
  });

export const TextField = styled(MuiTextField)`
  padding-top: 0;
  padding-bottom: 0;
  font-size: ${({ theme }) => theme.typography.body2.fontSize}px;

  .MuiFormLabel-root {
    top: -8px;
    left: 2px;
    padding-top: 0;
    padding-bottom: 0;
    font-size: ${({ theme }) => theme.typography.body2.fontSize}px;
  }

  & label.Mui-focused {
    color: rgba(0, 0, 0, 0.87);
  }

  & .MuiOutlinedInput-root {
    border: 0;
    border-color: ${GREY_219};
    fieldset.MuiOutlinedInput-notchedOutline {
      box-shadow: 0 2px 8px 0 rgba(25, 25, 25, 0.1);
    }
  }
` as typeof MuiTextField;

export const TimeTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    width: 6.5rem;
    padding-right: 0 !important;
  }
`;

export const StyledAutocomplete = styled(Autocomplete)`
  height: 32px;
  max-height: 32px;

  & + .MuiAutocomplete-popper.MuiAutocomplete-popperDisablePortal {
    min-width: auto;
    .MuiPaper-root {
      margin: ${SPACING_1} 0 !important;
    }
  }
  & .MuiAutocomplete-hasClearIcon {
    padding-right: 0;
  }

  .MuiFormControl-root,
  MuiInputBase-root {
    max-height: 32px;
  }
  &.hideEndAdornment .MuiButtonBase-root {
    display: none;
  }

  .time .MuiAutocomplete-input {
    padding-top: 9px;
    padding-bottom: 9px;
  }

  & [role='listbox'] {
    max-height: 150px !important;
    overflow: hidden;
  }

  .MuiInputBase-root,
  .MuiAutocomplete-input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: ${({ theme }) => theme.typography.body2.fontSize}px !important;
    color: ${({ theme }) => theme.palette.common.black} !important;
    height: 32px;
    max-height: 32px;
  }

  .MuiAutocomplete-inputRoot .MuiAutocomplete-input {
    height: 32px;
    max-height: 32px;
  }
` as typeof Autocomplete;

export const AutocompleteWrapper = styled('div')`
  display: inline-block;
  margin-left: 21px;

  & ul {
    padding-left: ${({ theme }) => theme.spacing(1)};
    padding-right: ${({ theme }) => theme.spacing(1)};
  }

  & #studentName-popup {
    max-height: 175px;
  }
  & #lessonType-popup {
    max-height: 175px;
  }
  & #time-popup {
    max-height: 210px;
  }
`;

export const Option = styled('span')`
  font-size: ${({ theme }) => theme.typography.body2.fontSize}px;
`;

const ChevronIconWrapper = styled('span')`
  position: absolute;
  right: 14px;
  top: 4px;
  transform: rotate(90deg);
`;

export const ChevronEndAdornment = ({ disabled }: { disabled?: boolean }) => {
  const theme = useTheme();

  return (
    <ChevronIconWrapper>
      <Icon icon="chevron" color={disabled ? theme.palette.grey[400] : 'currentcolor'} />
    </ChevronIconWrapper>
  );
};
